import type { RouteLocationNormalized } from 'vue-router'

import { ApplicationState } from '@/enums/ApplicationState'
import { useAppStore } from '@/stores/app'

/**
 * A tab that has been open across a deploy still references the previous build's chunk
 * hashes. Navigating to a lazily-imported route then rejects with one of these messages
 * instead of loading the route.
 */
const DYNAMIC_IMPORT_ERROR_PATTERN = /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed/i

/** Session-scoped flag guarding against reloading forever if the failure persists. */
const RELOAD_GUARD_KEY = 'atabix:dynamic-import-reload'

export function isDynamicImportError(error: Error): boolean {
  return DYNAMIC_IMPORT_ERROR_PATTERN.test(error.message)
}

/**
 * Recovers from a failed dynamic import of a route's lazy-loaded chunk. The standard
 * remedy is a one-time reload to pick up the new assets, landing on the route that was
 * originally being navigated to. If the reload fails again for the same reason, the
 * guard stops a second automatic reload and puts the app in a visible FATAL state
 * instead of leaving the failed navigation without any feedback.
 */
export function handleDynamicImportError(error: Error, to: RouteLocationNormalized): void {
  if (!isDynamicImportError(error)) return

  if (sessionStorage.getItem(RELOAD_GUARD_KEY)) {
    console.error('[ATABASE] Dynamic import failed again after a reload; not retrying to avoid a reload loop.', error)

    const app = useAppStore()
    app.setError(error.message)
    app.setState(ApplicationState.FATAL)
    return
  }

  sessionStorage.setItem(RELOAD_GUARD_KEY, '1')
  window.location.assign(to.fullPath)
}

/** Clears the reload guard after a successful navigation, so a later deploy can trigger a fresh one-time reload. */
export function clearDynamicImportReloadGuard(): void {
  sessionStorage.removeItem(RELOAD_GUARD_KEY)
}
